import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { useAuth } from "@/_core/hooks/useAuth";
import { trpc } from "@/lib/trpc";
import { Link } from "wouter";
import { useState } from "react";
import { toast } from "sonner";
import { StopWorkDialog } from "./RoWorkTimerCard";
import { useLiveElapsed } from "./useLiveElapsed";

export function FloatingRoWorkTimers() {
  const { user } = useAuth();
  const utils = trpc.useUtils();
  const active = trpc.roWorkSessions.myActive.useQuery(undefined, {
    enabled: Boolean(user),
    refetchInterval: 15_000,
  });
  const sessions = active.data ?? [];
  const promptSessions = sessions.filter((session) => session.continuationPromptDue);
  const [stopSessionId, setStopSessionId] = useState<number | null>(null);
  const confirm = trpc.roWorkSessions.confirmContinuation.useMutation({
    onSuccess: async () => {
      await utils.roWorkSessions.myActive.invalidate();
    },
    onError: (error) => toast.error(error.message || "Continuation was not saved."),
  });
  const stopTarget = sessions.find((session) => session.sessionId === stopSessionId) ?? null;

  if (!user || sessions.length === 0) return null;

  return (
    <>
      <aside
        data-testid="ro-active-timers"
        className="fixed bottom-4 right-4 z-50 w-[min(100vw-2rem,24rem)] rounded-2xl bg-[#0f1c3f] p-4 text-white shadow-xl pb-[max(1rem,env(safe-area-inset-bottom))]"
      >
        <div className="flex items-center justify-between gap-3">
          <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-300">Active repair timers</p>
          <p className="text-[11px] font-semibold uppercase tracking-wide text-emerald-300">{sessions.length} running</p>
        </div>
        <ul className="mt-3 space-y-3">
          {sessions.map((session) => (
            <TimerRow key={session.sessionId} session={session} onStop={() => setStopSessionId(session.sessionId)} />
          ))}
        </ul>
      </aside>
      <Dialog open={promptSessions.length > 0}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Continue working?</DialogTitle>
            <DialogDescription>
              It is 6:00 PM Singapore time. Confirm each timer you are still working on. Unconfirmed timers stop at 6:15 PM.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            {promptSessions.map((session) => (
              <div key={session.sessionId} className="rounded-md border p-3">
                <p className="text-sm font-medium">{session.jobNumber}</p>
                <p className="text-xs text-muted-foreground">{session.equipment} · {session.customerName}</p>
                <div className="mt-2 flex gap-2">
                  <Button type="button" className="bg-green-600 text-white hover:bg-green-700" disabled={confirm.isPending} onClick={() => confirm.mutate({ sessionId: session.sessionId })}>
                    YES — continue working
                  </Button>
                  <Button type="button" variant="outline" onClick={() => setStopSessionId(session.sessionId)}>STOP NOW</Button>
                </div>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
      {stopTarget ? (
        <StopWorkDialog open session={stopTarget} jobNumber={stopTarget.jobNumber} onOpenChange={(open) => { if (!open) setStopSessionId(null); }} />
      ) : null}
    </>
  );
}

function TimerRow({
  session,
  onStop,
}: {
  session: {
    sessionId: number;
    repairOrderId: number;
    jobNumber: string;
    equipment: string;
    customerName: string;
    startedAt: Date | string;
  };
  onStop: () => void;
}) {
  const elapsed = useLiveElapsed(session.startedAt);
  return (
    <li className="border-t border-white/10 pt-3 first:border-t-0 first:pt-0">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <Link href={`/repair-orders/id/${session.repairOrderId}`} className="text-sm font-semibold text-white underline">
            {session.jobNumber}
          </Link>
          <p className="truncate text-xs text-slate-300">{session.equipment}</p>
          <p className="truncate text-xs text-slate-400">{session.customerName}</p>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <p className="font-mono text-sm" aria-live="polite">{elapsed}</p>
          <Button type="button" size="sm" aria-label={`Stop ${session.jobNumber}`} className="bg-red-500 text-white hover:bg-red-600" onClick={onStop}>STOP</Button>
        </div>
      </div>
    </li>
  );
}

export function AdminRecoverControl({ sessionId }: { sessionId: number }) {
  const utils = trpc.useUtils();
  const [notes, setNotes] = useState("");
  const recover = trpc.roWorkSessions.adminRecover.useMutation({
    onSuccess: async () => {
      setNotes("");
      await utils.roWorkSessions.wip.invalidate();
      await utils.roWorkSessions.myActive.invalidate();
    },
    onError: (error) => toast.error(error.message || "Recovery stop failed."),
  });
  return (
    <div className="mt-2 space-y-2">
      <Textarea value={notes} onChange={(event) => setNotes(event.target.value)} placeholder="Why this timer is being stopped" />
      <Button type="button" size="sm" variant="outline" disabled={recover.isPending || !notes.trim()} onClick={() => recover.mutate({ sessionId, notes })}>
        Admin recovery stop
      </Button>
    </div>
  );
}
